import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import classnames from "classnames";
import { LazyQueryHookOptions, QueryTuple } from "@apollo/client";

import {
  PaginatedVariables,
  SearchableVariables,
  Collection,
} from "../../../../../graphql/types";

import { useWombatTheme } from "../../../theme";

import { InputProps } from ".";
import { ErrorIcon } from "../../../../icons";

// FIXME: Should come from a dedicated file
import { ReactHookFormProps } from "../Select";
import { TailSpin } from "../../Loader/TailSpin";
import useDebounce from "../../../../../hooks/useDebounce";
import { isEqual } from "lodash";
import useTranslations from "../../../../../hooks/useTranslations";
import useOnClickOutside from "../../../../../hooks/useOnClickOutside";

type AutoCompleteProps<
  T,
  QVar extends SearchableVariables & PaginatedVariables
> = InputProps & {
  /**
   * react-hook-form specific options
   * If provided, registration and value will be set according to the getValue callback return
   * @default undefined
   */
  RHF?: ReactHookFormProps;
  /**
   * Object used to query the backend ressources
   * The return value of the provided hook is expected to have the form:
   * ```ts
   *  {
   *    [accessKey]: Collection<T>
   *  }
   * ```
   */
  query: {
    /**
     * The key to access to the `Collection`
     * Example for `useGetSkills`
     * ```ts
     * {
     *   data: {
     *     skills: // <--- This is the accessKey
     *     {
     *        collection: Skill[]
     *        ...
     *     }
     *   }
     * }
     * ```
     */
    accessKey: string;
    /**
     * We infer `q: searchValue, page: 1, per: 20` as hooks options
     * Every other options should be passed inside this object and will be appended the the options payload
     */
    hookOptions?: Record<string, unknown>;
    /**
     * The actual hook to be used
     */
    hook: (
      options?: LazyQueryHookOptions<{ skills: Collection<T> }, QVar>
    ) => QueryTuple<{ skills: Collection<T> }, QVar>;
  };
  /**
   *  Using listStrict prevents the user from selecting an element not coming from the fetched list
   *  @default true
   */
  listStrict: boolean;
  /**
   * This callback will be called on each change, useful for controlled input
   * Think of outside a react-hook-form context
   */
  onChange: (item: T) => void;
  /**
   * Callback that given a T return a meaningfull string that represent the ressource
   */
  getLabel: (data: T) => string;
  /**
   * This callback will be used to set the value inside RHF as well as internal purposes
   */
  getValue: (data: T) => string;
  /**
   * Default value, will be set to the react-hook-form context as well
   */
  defaultValue?: T;
  /**
   * This function is called to render elements inside the select dropdown
   */
  renderItem: (item: T) => string;
  /**
   * Used to render an empty search
   * @default t('wombat.select.emptyResult')
   */
  emptyResult?: () => React.ReactNode;
  /**
   * A ref to the internal input used
   */
  ref:
    | ((instance: HTMLInputElement) => void)
    | React.MutableRefObject<HTMLInputElement>;
};

const AutoComplete = <
  T,
  QVar extends SearchableVariables & PaginatedVariables
>({
  label,
  help,
  description,
  size,
  type = "text",
  id,
  listStrict = true,
  srOnlyLabel = false,
  error = { type: "required" },
  disabled = false,
  defaultValue,
  onChange,
  query,
  getLabel,
  getValue,
  renderItem,
  emptyResult,
  RHF,
  ref,
  ...props
}: AutoCompleteProps<T, QVar>) => {
  const t = useTranslations();
  const { theme } = useWombatTheme();

  useEffect(() => {
    if (RHF) {
      RHF?.register(RHF.name, RHF.registerOptions);
      if (defaultValue) {
        RHF?.setValue(RHF.name, getValue(defaultValue), RHF.setValueOptions);
      } else {
        RHF?.setValue(RHF.name, undefined, RHF.setValueOptions);
      }
    }
  }, [RHF?.register]);

  const labelStyle = classnames(
    theme.form.input.label.base,
    disabled ? theme.form.input.label.disabled : theme.form.input.label.able,
    srOnlyLabel && "sr-only"
  );
  const descriptionStyle = classnames(theme.form.input.description);
  const containerStyle = classnames(
    size && theme.form.input.size[size],
    "relative mt-1 rounded-md shadow-sm"
  );
  const inputStyle = classnames(
    theme.form.input.base,
    theme.form.input.status[error?.message ? "error" : "default"],
    size && theme.form.input.size[size]
  );

  const iconStyle = classnames(theme.form.input.icon.error);
  const helpStyle = classnames(
    theme.form.input.help[error?.message ? "error" : "default"]
  );

  const _emptyResult = useMemo(
    () => (emptyResult ? emptyResult : () => t("wombat.select.emptyResult")),
    [emptyResult]
  );

  const [hook, { data, loading, called }] = query.hook();

  const results = useMemo(() => data?.[query.accessKey]?.collection || [], [data]);
  const [value, setValue] = useState(
    defaultValue ? getLabel(defaultValue) : ""
  );
  const _onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setValue(e.target.value)
    },
    []
  );

  const [_item, setItem] = useState<T>(defaultValue || null);

  const [dropOpen, setDropOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const handleClickOutside = useCallback(() => {
    if (listStrict && !(_item && getLabel(_item) === value)) {
      setValue("");
      if (RHF) {
        RHF.setValue(RHF.name, undefined, RHF.setValueOptions);
      }
    }
    setDropOpen(false);
  }, [_item, RHF]);

  useOnClickOutside(popoverRef, handleClickOutside);

  const debouncedValue = useDebounce(value, 500);
  useEffect(() => {
    if (debouncedValue.length > 1)
      hook({
        variables: { ...query.hookOptions, q: debouncedValue, page: 1, per: 20 },
      });
  }, [debouncedValue, query, hook]);

  const handleItemSelected = useCallback(
    (item: T) => {
      const itemLabel = getLabel(item);
      const itemValue = getValue(item);

      if (!isEqual(debouncedValue, itemLabel)) {
        setValue(itemLabel);
        if (onChange) {
          onChange(item);
        }
        if (RHF) {
          RHF.setValue(RHF.name, itemValue, RHF.setValueOptions);
        }
      }
      setItem(item);
      setDropOpen(false);
    },
    [_item, RHF]
  );

  return (
    <div data-test-id={`input_${id}_group`} ref={popoverRef}>
      {label && (
        <label
          className={labelStyle}
          htmlFor={id}
          data-test-id={`input_${id}_label`}
        >
          {label}
          {description && <p className={descriptionStyle}>{description}</p>}
        </label>
      )}
      <div className={containerStyle}>
        <input
          {...props}
          type={type}
          autoComplete="off"
          onFocus={() => setDropOpen(true)}
          disabled={disabled}
          aria-disabled={disabled ? "true" : "false"}
          className={inputStyle}
          data-test-id={`input_${id}`}
          onChange={_onChange}
          value={value}
          ref={ref}
          id={id}
        />
        {error?.message && (
          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
            <ErrorIcon iconClassName={iconStyle} />
          </div>
        )}
        {called && (
          <div className="relative">
            <div
              className={`${
                dropOpen ? "block" : "hidden"
              } absolute inset-0 z-10 w-full mt-1 bg-white rounded-md shadow-lg max-h-56`}
            >
              {!loading && (
                <ul
                  tabIndex={-1}
                  role="listbox"
                  className="flex flex-col py-1 overflow-auto text-base bg-white rounded-md ring-1 ring-black ring-opacity-5 max-h-56 focus:outline-none sm:text-sm"
                >
                  {/* NOTE: Will probably need to be virtualized when huge data set is passed */}
                  {results.length > 0 &&
                    results.map((item) => (
                      <li
                        role="button"
                        onClick={() => handleItemSelected(item)}
                        className={
                          theme.form.select.item[
                            value === getValue(item) ? "selected" : "base"
                          ]
                        }
                        key={getValue(item)}
                      >
                        {renderItem(item)}
                      </li>
                    ))}
                  {results.length === 0 && _emptyResult()}
                </ul>
              )}
              {loading && (
                <div className="flex flex-col items-center justify-center flex-grow w-full h-32 bg-white rounded-md shadow-md">
                  <TailSpin className="w-6 h-6 text-primary-600" />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      {(help || error?.message) && (
        <p
          className={helpStyle}
          data-test-id={`input_${id}_${
            error?.message ? "error_message" : "help_message"
          }`}
        >
          {error?.message ? error?.message : help}
        </p>
      )}
    </div>
  );
};

export default React.memo(AutoComplete);
